/**
 * Audit Log Table
 *
 * Displays audit log entries with expandable rows for change details.
 */

import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Button,
  Skeleton,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faAnglesLeft } from "@fortawesome/pro-solid-svg-icons/faAnglesLeft";
import { faAnglesRight } from "@fortawesome/pro-solid-svg-icons/faAnglesRight";
import type { AuditLogEntry } from "../../hooks/useAuditLog";

interface AuditLogTableProps {
  entries: AuditLogEntry[];
  isLoading: boolean;
  page: number;
  pageCount: number;
  total: number;
  onPageChange: (page: number) => void;
  showOrgColumn?: boolean;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function getActionBadgeVariant(action: string): "default" | "secondary" | "destructive" | "outline" {
  switch (action) {
    case "create":
      return "default";
    case "delete":
      return "destructive";
    case "update":
    case "publish":
    case "restore":
      return "secondary";
    default:
      return "outline";
  }
}

function getSourceBadgeVariant(source: "content" | "security"): "default" | "secondary" {
  return source === "security" ? "default" : "secondary";
}

export function AuditLogTable({
  entries,
  isLoading,
  page,
  pageCount,
  total,
  onPageChange,
  showOrgColumn = false,
}: AuditLogTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No audit log entries found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]" />
            <TableHead>Time</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Entity</TableHead>
            {showOrgColumn && <TableHead>Org</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <AuditLogRow key={entry.id} entry={entry} showOrgColumn={showOrgColumn} />
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {total} total entries
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(1)}
            disabled={page === 1}
          >
            <FontAwesomeIcon icon={faAnglesLeft} className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground px-2">
            {page} / {pageCount || 1}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
          >
            <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(pageCount)}
            disabled={page >= pageCount}
          >
            <FontAwesomeIcon icon={faAnglesRight} className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface AuditLogRowProps {
  entry: AuditLogEntry;
  showOrgColumn: boolean;
}

function AuditLogRow({ entry, showOrgColumn }: AuditLogRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <>
        <TableRow className={hasChanges ? "cursor-pointer" : ""}>
          <TableCell>
            {hasChanges && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  {isOpen ? (
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4" />
                  ) : (
                    <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            )}
          </TableCell>
          <TableCell className="text-sm">{formatDate(entry.createdAt)}</TableCell>
          <TableCell>
            <Badge variant={getSourceBadgeVariant(entry.source)} className="text-xs">
              {entry.source}
            </Badge>
          </TableCell>
          <TableCell className="text-sm">{entry.userEmail ?? "System"}</TableCell>
          <TableCell>
            <Badge variant={getActionBadgeVariant(entry.action)} className="text-xs">
              {entry.action}
            </Badge>
          </TableCell>
          <TableCell className="text-sm">
            <span className="font-medium">{entry.entityType}</span>
            {entry.entityId && (
              <span className="text-muted-foreground ml-1 text-xs">
                {entry.entityId.slice(0, 8)}...
              </span>
            )}
          </TableCell>
          {showOrgColumn && (
            <TableCell className="text-sm">{entry.orgName ?? "-"}</TableCell>
          )}
        </TableRow>
        {hasChanges && (
          <CollapsibleContent asChild>
            <tr>
              <td colSpan={showOrgColumn ? 7 : 6} className="p-0">
                <div className="bg-muted/50 px-8 py-4">
                  <ChangesDisplay changes={entry.changes!} />
                </div>
              </td>
            </tr>
          </CollapsibleContent>
        )}
      </>
    </Collapsible>
  );
}

interface ChangesDisplayProps {
  changes: Record<string, unknown>;
}

function ChangesDisplay({ changes }: ChangesDisplayProps) {
  // Check if it's a before/after format
  const hasBefore = "before" in changes;
  const hasAfter = "after" in changes;

  if (hasBefore || hasAfter) {
    const before = changes.before as Record<string, unknown> | undefined;
    const after = changes.after as Record<string, unknown> | undefined;
    const allKeys = new Set([
      ...Object.keys(before ?? {}),
      ...Object.keys(after ?? {}),
    ]);

    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium mb-2">Changes</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-xs text-muted-foreground font-medium">Before</span>
          </div>
          <div>
            <span className="text-xs text-muted-foreground font-medium">After</span>
          </div>
        </div>
        {[...allKeys].map((key) => {
          const beforeVal = before?.[key];
          const afterVal = after?.[key];
          if (beforeVal === afterVal) return null;
          return (
            <div key={key} className="grid grid-cols-2 gap-4 text-sm border-t pt-2">
              <div>
                <span className="text-muted-foreground">{key}:</span>{" "}
                <code className="text-xs bg-red-100 dark:bg-red-900/30 px-1 rounded">
                  {formatValue(beforeVal)}
                </code>
              </div>
              <div>
                <span className="text-muted-foreground">{key}:</span>{" "}
                <code className="text-xs bg-green-100 dark:bg-green-900/30 px-1 rounded">
                  {formatValue(afterVal)}
                </code>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Raw display
  return (
    <div className="space-y-1">
      <h4 className="text-sm font-medium mb-2">Details</h4>
      <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
        {JSON.stringify(changes, null, 2)}
      </pre>
    </div>
  );
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return "null";
  if (typeof val === "string") return `"${val}"`;
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}
